import type { RichText } from "@storytell/cp-client";
import {
  type PmBlockquote,
  type PmBulletList,
  type PmCitation,
  type PmCodeBlock,
  type PmDoc,
  type PmHeading,
  type PmHorizontalRule,
  type PmInline,
  type PmListItem,
  type PmMark,
  PmMarkType,
  type PmNode,
  PmNodeType,
  type PmOrderedList,
  type PmParagraph,
  type PmTable,
  type PmTableCell,
  type PmTableHeader,
  type PmTableRow,
  type PmText,
  RichTextType,
  VariableKinds,
} from "@storytell/types/richText";

const parseMark = (mark: RichText.Mark): PmMark | undefined => {
  switch (mark.type) {
    case RichTextType.BoldType:
      return { type: PmMarkType.Bold };
    case RichTextType.ItalicType:
      return { type: PmMarkType.Italic };
    case RichTextType.StrikeType:
      return { type: PmMarkType.Strike };
    case RichTextType.CodeType:
      return { type: PmMarkType.Code };
    case RichTextType.LinkType:
      if (mark.attrs.case === "linkAttrs") {
        return { type: PmMarkType.Link, attrs: { href: mark.attrs.value.href } };
      }
      return { type: PmMarkType.Link, attrs: { href: "#" } };
  }
  return undefined;
};

const parseText = (node: RichText.Node): PmText | undefined =>
  node.text
    ? {
        type: PmNodeType.Text,
        text: node.text || "",
        marks: node.marks.map(parseMark).filter(Boolean) as PmMark[],
      }
    : undefined;
const parseCitation = (node: RichText.Node): PmCitation => ({
  type: PmNodeType.Citation,
  attrs: {
    value: node.attrs.case === "citationAttrs" ? node.attrs.value.value : "",
  },
});

const parseInlineNodes = (content: RichText.Node[]): PmInline[] =>
  content
    .map((node) => {
      switch (node.type) {
        case RichTextType.TextType:
          return parseText(node);
        case RichTextType.MustacheVariableType:
          return parseVariable(node);
        case RichTextType.CitationType:
          return parseCitation(node);
      }

      return undefined as unknown as PmInline;
    })
    .filter(Boolean) as PmInline[];

const parseVariable = (node: RichText.Node) => {
  const attrs = node.attrs?.case === "variableAttrs" ? node.attrs.value : undefined;

  if (!attrs) return undefined;

  const type = VariableKinds.find((kind) => attrs.value?.startsWith(`${kind}_`)) ?? "unknown";
  if (!type) return undefined;

  return {
    type: PmNodeType.VariableV1,
    attrs: {
      type,
      id: attrs.value ?? "",
      text: attrs.text ?? "",
    },
  };
};

const parseParagraph = (node: RichText.Node) => {
  const first = node.content[0];
  if (node.content.length === 1 && first?.type === RichTextType.MustacheVariableType) {
    return parseVariable(first);
  }

  const paragraph: PmParagraph = {
    type: PmNodeType.Paragraph,
    content: parseInlineNodes(node.content),
  };
  return paragraph;
};

const parseHeading = (node: RichText.Node): PmHeading => ({
  type: PmNodeType.Heading,
  attrs: {
    level: (node.attrs?.case === "headingAttrs" ? Math.min(Math.max(node.attrs.value.level, 1), 6) : 1) as
      | 1
      | 2
      | 3
      | 4
      | 5
      | 6,
  },
  content: parseInlineNodes(node.content),
});

const parseListItem = (node: RichText.Node): PmListItem => ({
  type: PmNodeType.ListItem,
  content: parseBlockNodes(node.content),
});

const parseList = (node: RichText.Node): PmBulletList | PmOrderedList => {
  const listType = node.attrs?.case === "listAttrs" ? node.attrs.value.listType : "bullet";
  return {
    type: listType === "ordered" ? PmNodeType.OrderedList : PmNodeType.BulletList,
    content: node.content.map(parseListItem),
  };
};

const parseBlockquote = (node: RichText.Node): PmBlockquote => ({
  type: PmNodeType.Blockquote,
  content: parseBlockNodes(node.content),
});

const parseCodeBlock = (node: RichText.Node): PmCodeBlock => ({
  type: PmNodeType.CodeBlock,
  attrs: {
    language: node.attrs?.case === "codeBlockAttrs" ? node.attrs.value.language : undefined,
  },
  content: node.content.map(parseText).filter((t): t is PmText => t !== undefined),
});

const parseTableCell = <T extends boolean>(
  node: RichText.Node,
  isHeader: T,
): T extends true ? PmTableHeader : PmTableCell =>
  ({
    type: isHeader ? PmNodeType.TableHeader : PmNodeType.TableCell,
    attrs:
      node.attrs?.case === "tableCellAttrs"
        ? {
            colspan: node.attrs.value.colspan,
            rowspan: node.attrs.value.rowspan,
            alignment: node.attrs.value.alignment,
          }
        : undefined,
    content: [{ type: "paragraph", content: parseInlineNodes(node.content) }],
  }) as T extends true ? PmTableHeader : PmTableCell;

const parseTableRow = (node: RichText.Node): PmTableRow => {
  const isHeader = node.type === RichTextType.TableHeaderType;
  return {
    type: PmNodeType.TableRow,
    content: node.content.map((node) => parseTableCell(node, isHeader)) as PmTableCell[] | PmTableHeader[],
  };
};

const parseTable = (node: RichText.Node): PmTable => ({
  type: PmNodeType.Table,
  content: node.content.map(parseTableRow),
});

const parseHorizontalRule = (node: RichText.Node): PmHorizontalRule => ({
  type: PmNodeType.HorizontalRule,
  attrs:
    node.attrs?.case === "hrAttrs"
      ? {
          style: node.attrs.value.style,
        }
      : undefined,
});

const parseBlockNodes = (content: RichText.Node[]): PmNode[] =>
  content
    .map((node) => {
      switch (node.type) {
        case RichTextType.MustacheVariableType:
          return parseVariable(node);
        case RichTextType.ParagraphType:
          return parseParagraph(node);
        case RichTextType.HeadingType:
          return parseHeading(node);
        case RichTextType.ListType:
          return parseList(node);
        case RichTextType.BlockquoteType:
          return parseBlockquote(node);
        case RichTextType.CodeBlockType:
          return parseCodeBlock(node);
        case RichTextType.TableType:
          // TODO: Implement table parsing
          // return undefined as unknown as PmNode;
          return parseTable(node);
        case RichTextType.HorizontalRuleType:
          return parseHorizontalRule(node);
      }

      return undefined as unknown as PmNode;
    })
    .filter(Boolean) as PmNode[];

export const richTextToProseMirror = (richText: RichText.RichText): PmDoc => ({
  type: PmNodeType.Doc,
  content: parseBlockNodes(richText.content),
});
